import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { CATEGORIES, drinksIn, price, chipsFor } from "@/content/menu";
import { FlavorCup } from "@/components/site/FlavorCup";
import { OrderButton } from "@/components/site/OrderContext";
import { MaskLines, EASE } from "@/components/site/motion";

const SHOW = CATEGORIES.filter((c) => c.slug !== "snacks");
const CHIP_POS = ["left-4 top-6 sm:left-8 sm:top-10", "right-4 top-[38%] sm:right-8", "left-6 bottom-8 sm:left-10 sm:bottom-12"];

const Stage = ({ drink, color }) => (
  <motion.div data-testid="showcase-stage" className="grain relative aspect-square w-full overflow-hidden rounded-[2.5rem] sm:aspect-[5/4] lg:aspect-auto lg:h-full lg:min-h-[620px]" animate={{ backgroundColor: drink.color || color }} transition={{ duration: 0.7, ease: EASE }} style={{ backgroundColor: color }}>
    <AnimatePresence>
      <motion.div key={drink.id} className="absolute inset-0 flex items-center justify-center p-10" initial={{ opacity: 0, scale: 0.85, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} exit={{ opacity: 0, scale: 1.08 }} transition={{ duration: 0.7, ease: EASE }}>
        {drink.image ? (
          <img src={drink.image} alt={`${drink.name} from TeaDM`} className="h-[86%] w-[86%] rounded-[2rem] object-cover shadow-[0_40px_70px_-25px_rgba(0,0,0,0.6)]" />
        ) : (
          <FlavorCup color={drink.color} label={drink.name} className="h-[78%] w-auto drop-shadow-[0_30px_40px_rgba(0,0,0,0.35)]" />
        )}
      </motion.div>
    </AnimatePresence>
    <AnimatePresence>
      {chipsFor(drink).map((c, i) => (
        <motion.span key={drink.id + c} className={`absolute z-10 max-w-[60%] rounded-full bg-[#fff5e8] px-4 py-2 font-display text-xs font-bold uppercase tracking-wide text-[#1a0a10] shadow-lg ${CHIP_POS[i]}`} initial={{ opacity: 0, y: 16, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ delay: 0.25 + i * 0.1, duration: 0.5, ease: EASE }}>
          {c}
        </motion.span>
      ))}
    </AnimatePresence>
  </motion.div>
);

export const DrinkShowcase = () => {
  const [cat, setCat] = useState(SHOW[2].slug);
  const [di, setDi] = useState(0);
  const category = SHOW.find((c) => c.slug === cat);
  const list = drinksIn(cat);
  const drink = list[di] || list[0];

  const pick = (slug) => {
    setCat(slug);
    setDi(0);
  };

  return (
    <section data-testid="drink-showcase" className="bg-[#fff5e8] py-24 sm:py-32">
      <div className="wrap">
        <div className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="eyebrow text-[#ff2e63]">The tracklist</p>
            <MaskLines lines={["Press play on", "your next sip."]} className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
          </div>
          <p className="max-w-sm text-base text-[#1a0a10]/75">Hover or tap any drink to bring it on stage. Every name, price and recipe is straight from the TeaDM menu.</p>
        </div>

        <div className="no-scrollbar -mx-5 mb-8 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist">
          {SHOW.map((c) => (
            <button key={c.slug} type="button" role="tab" aria-selected={c.slug === cat} onClick={() => pick(c.slug)} data-testid={`showcase-tab-${c.slug}`} className={`shrink-0 rounded-full border-2 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${c.slug === cat ? "border-transparent text-white" : "border-[#1a0a10]/15 text-[#1a0a10] hover:border-[#1a0a10]"}`} style={c.slug === cat ? { background: c.color } : undefined}>
              {c.name}
            </button>
          ))}
        </div>

        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7"><Stage drink={drink} color={category.color} /></div>
          <div className="flex flex-col lg:col-span-5">
            <p className="mb-4 text-[#1a0a10]/75">{category.blurb}</p>
            <ol className="divide-y divide-[#1a0a10]/10 border-y border-[#1a0a10]/10" data-lenis-prevent>
              {list.map((x, i) => (
                <li key={x.id}>
                  <button type="button" onMouseEnter={() => setDi(i)} onFocus={() => setDi(i)} onClick={() => setDi(i)} data-testid={`showcase-drink-${x.id}`} className={`group flex w-full items-center gap-4 py-3.5 text-left transition-colors duration-300 ${i === di ? "text-[#1a0a10]" : "text-[#1a0a10]/65 hover:text-[#1a0a10]"}`}>
                    <span className="w-7 font-display text-xs font-bold">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1 font-display text-lg font-bold leading-tight sm:text-xl">{x.name}</span>
                    <span className="font-display text-sm font-bold">{price(x.price)}</span>
                    <span className="h-3 w-3 rounded-full transition-transform duration-300 group-hover:scale-125" style={{ background: i === di ? category.color : "transparent", border: `2px solid ${category.color}` }} />
                  </button>
                </li>
              ))}
            </ol>
            <AnimatePresence mode="popLayout">
              <motion.p key={drink.id} className="mt-6 text-[15px] text-[#1a0a10]/80" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                {drink.desc || drink.specs.join(" · ") || `${drink.name} — ${price(drink.price)}`}
              </motion.p>
            </AnimatePresence>
            <div className="mt-auto flex flex-wrap gap-3 pt-8">
              <Link to={`/menu#${cat}`} className="btn btn-ink" data-testid="showcase-discover-menu-btn">Discover the menu <ArrowRight className="h-4 w-4" /></Link>
              <OrderButton testId="showcase-order-online-btn" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
